import { createApi } from '@reduxjs/toolkit/query/react';

import { apiDocumentBatchesApi } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

// Types
export type DocumentBatchStatusTag = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';

export interface DocumentBatchStatus {
  tag: DocumentBatchStatusTag;
  name: string;
}

export interface DocumentBatch {
  id: string;
  reference?: string | null;
  status: DocumentBatchStatus;
  downloaded_at?: string | null;
  created_at: string;
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  // Reason the batch's failed job could not be produced. Optional: older API responses omit it.
  error_message?: string | null;
}

export interface CreateDocumentBatchJob {
  document_type: string;
  document_id?: string;
  document_ids?: string[];
}

export const typePrefix = 'documentBatches';

export const documentBatchesApi = createApi({
  reducerPath: 'documentBatchesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiDocumentBatchesApi }),
  tagTypes: ['documentBatches'],
  endpoints: (builder) => ({
    getDocumentBatches: builder.query<DocumentBatch[], void>({
      query: () => ({ url: '', method: 'GET' }),
      providesTags: [{ type: 'documentBatches', id: 'LIST' }],
      transformResponse: (payload) => unwrap<DocumentBatch[]>(payload),
    }),
    createDocumentBatch: builder.mutation<DocumentBatch, CreateDocumentBatchJob[]>({
      query: (body: CreateDocumentBatchJob[]) => ({ url: '', method: 'POST', body }),
      invalidatesTags: [{ type: 'documentBatches', id: 'LIST' }],
      transformResponse: (payload) => unwrap<DocumentBatch>(payload),
    }),
    cancelDocumentBatch: builder.mutation<DocumentBatch, string>({
      query: (id) => ({ url: `/${id}/cancel`, method: 'POST' }),
      invalidatesTags: [{ type: 'documentBatches', id: 'LIST' }],
      transformResponse: (payload) => unwrap<DocumentBatch>(payload),
    }),
    retryDocumentBatch: builder.mutation<DocumentBatch, string>({
      query: (id) => ({ url: `/${id}/retry`, method: 'POST' }),
      invalidatesTags: [{ type: 'documentBatches', id: 'LIST' }],
      transformResponse: (payload) => unwrap<DocumentBatch>(payload),
    }),
  }),
});

export const {
  useLazyGetDocumentBatchesQuery,
  useCreateDocumentBatchMutation,
  useCancelDocumentBatchMutation,
  useRetryDocumentBatchMutation,
} = documentBatchesApi;
